import { pdkAxios } from '@/lib/pdk-axios';
import { dealStore } from '@/models/stores';
import { taskStoreV2 } from '@/models/stores/tasks-v2.ts';
import type { AssignedTask, TaskableType, TaskV2 } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const useAssignTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: async ({
      task,
      dealId,
    }: {
      task: AssignedTask;
      dealId: string;
    }) => {
      dealStore.upsertAssignedTask({
        id: dealId,
        assignedTask: {
          id: task.id,
          task_id: task.task_id,
          deal_id: dealId,
          title: task.title,
          description: task.description,
          status: task.status || 'incomplete',
        },
      });
    },
    mutationFn: async ({
      task,
      dealId,
    }: {
      task: AssignedTask;
      dealId: string;
    }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/tasks/assign`, {
          id: task.id,
          task_id: task.task_id,
          deal_id: dealId,
          title: task.title,
          description: task.description,
          status: task.status || 'incomplete',
        })
        .catch((e) => {
          console.error('Error assigning task', e);
          throw e;
        });

      return response.data;
    },
    onError: (error, variables) => {
      console.error('Failed to assign task', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// generalized version

export const useAssignTaskMutationV2 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: async ({
      task,
      taskableType,
      taskableId,
    }: {
      task: TaskV2;
      taskableType: TaskableType;
      taskableId: string;
    }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks'] });

      const previousTask = taskStoreV2.getById(task.id);

      taskStoreV2.upsert({
        ...task,
        taskable_type: taskableType,
        taskable_id: taskableId,
        status: task.status || 'incomplete',
      });

      return { previousTask };
    },
    mutationFn: async ({
      task,
      taskableType,
      taskableId,
    }: {
      task: TaskV2;
      taskableType: TaskableType;
      taskableId: string;
    }) => {
      const response = await pdkAxios
        .post(`/tasks/${taskableId}/assign`, {
          id: task.id,
          task_template_id: task.task_template_id,
          taskable_type: taskableType,
          taskable_id: taskableId,
          title: task.title,
          description: task.description,
          status: task.status || 'incomplete',
        })
        .catch((e) => {
          console.error('Error assigning task', e);
          throw e;
        });

      return response.data;
    },
    onError: (error, variables, context) => {
      console.error('Failed to assign task', error, variables);

      if (context?.previousTask) {
        taskStoreV2.upsert(context.previousTask);
      } else {
        taskStoreV2.remove(variables.task.id);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};
